/* Ban Intelligence — the Owner Deck's trust & safety war room (platform admin only).
   Every flag is evidence for a HUMAN decision: fingerprint similarity, VPN verdicts,
   honeypot trips, Steam trust, behaviour matches. Nothing here auto-bans. */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Fingerprint, Wifi, Crosshair, Gamepad2, BrainCircuit, Ban, EyeOff, CheckCircle2, Plus, Copy, AlertOctagon, RefreshCcw } from "lucide-react";
import api, { formatApiErrorDetail, safeCopy } from "@/lib/api";

const inputCls = "w-full bg-[#0A0A0C] border border-white/10 px-3 py-2.5 font-mono text-xs text-zinc-100 focus:outline-none focus:ring-1 focus:ring-nc-orange focus:border-nc-orange transition-all";
const card = "border border-white/[0.08] bg-[#0A0A0C] p-6 flex flex-col gap-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]";

const KIND_META = {
  fingerprint: { icon: Fingerprint, label: "Device fingerprint", cls: "text-nc-orange border-nc-orange/40 bg-nc-orange/10" },
  vpn: { icon: Wifi, label: "VPN / proxy", cls: "text-sky-400 border-sky-400/40 bg-sky-400/10" },
  honeypot: { icon: Crosshair, label: "Honeypot code", cls: "text-red-400 border-red-400/40 bg-red-400/10" },
  steam_trust: { icon: Gamepad2, label: "Steam low-trust", cls: "text-zinc-300 border-white/25 bg-white/5" },
  behavior: { icon: BrainCircuit, label: "Behaviour match", cls: "text-fuchsia-400 border-fuchsia-400/40 bg-fuchsia-400/10" },
};

export function BanIntelPanel() {
  const [intel, setIntel] = useState(null);
  const [reason, setReason] = useState({});
  const [hpLabel, setHpLabel] = useState("");
  const [breach, setBreach] = useState(null);
  const [breachBody, setBreachBody] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get("/security/intel").then((r) => setIntel(r.data)).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);
  useEffect(load, [load]);

  const act = async (userId, mode) => {
    const why = (reason[userId] || "").trim();
    if (mode !== "clear" && why.length < 3) { toast.error("Record a reason first — every action is logged"); return; }
    setBusy(userId + mode);
    try {
      const r = await api.post(`/security/users/${userId}/action`, { mode, reason: why });
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const dismiss = async (flagId) => {
    try { await api.post(`/security/flags/${flagId}/dismiss`); toast.success("Flag dismissed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const makeHoneypot = async () => {
    if (hpLabel.trim().length < 2) { toast.error("Label it — where are you planting this code?"); return; }
    try {
      const r = await api.post("/security/honeypots", { label: hpLabel.trim() });
      await safeCopy(r.data.code);
      toast.success(`Honeypot armed and copied: ${r.data.code}`);
      setHpLabel("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const runScan = async () => {
    setBusy("scan");
    try { const r = await api.post("/security/intel/behavior-scan"); toast.success(r.data.message); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const runSweep = async () => {
    setBusy("sweep");
    try { const r = await api.post("/security/retention/sweep"); toast.success(`Retention sweep: ${r.data.warned} warned, ${r.data.deleted} deleted`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const loadBreach = async () => {
    try {
      const r = await api.get("/security/breach/template");
      setBreach(r.data);
      setBreachBody(r.data.body);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendBreach = async () => {
    if (breachBody.trim().length < 20) { toast.error("Write the notice first"); return; }
    setBusy("breach");
    try {
      const r = await api.post("/security/breach/notify", { subject: breach.subject, body: breachBody, user_ids: [] });
      toast.success(r.data.message);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!intel) return <div className={card}><p className="font-mono text-xs text-zinc-500 animate-pulse uppercase tracking-[0.2em]">Pulling intelligence…</p></div>;

  return (
    <div className="flex flex-col gap-5">
      {/* flags */}
      <div className={card}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" /> Flagged accounts — {intel.flags.length} open · {intel.shadowbanned} shadowbanned network-wide
          </p>
          <button data-testid="behavior-scan-button" onClick={runScan} disabled={busy === "scan"}
            className="font-mono text-[9px] uppercase tracking-[0.15em] border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-1.5 flex items-center gap-1.5 transition-colors">
            <RefreshCcw className={`w-3 h-3 ${busy === "scan" ? "animate-spin" : ""}`} /> Run behaviour scan
          </button>
        </div>
        <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">
          Flags are evidence for human review only — no flag ever bans automatically. Fingerprint threshold: {intel.fingerprint_threshold}% similarity.
        </p>
        {intel.flags.length === 0 && <p className="font-mono text-xs text-zinc-600 border border-dashed border-white/10 p-6 text-center">No open flags. The city is quiet.</p>}
        {intel.flags.map((f) => {
          const meta = KIND_META[f.kind] || KIND_META.fingerprint;
          const Icon = meta.icon;
          const u = f.user || {};
          return (
            <div key={f.id} data-testid={`flag-${f.id}`} className="border border-white/10 bg-black/30 p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-mono text-[8px] uppercase tracking-[0.15em] border px-2 py-1 flex items-center gap-1.5 ${meta.cls}`}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </span>
                <span className="font-mono text-[10px] text-zinc-200">{u.email || "(deleted account)"}</span>
                {u.shadow && <span className="font-mono text-[8px] uppercase tracking-[0.15em] border border-fuchsia-400/40 text-fuchsia-400 px-2 py-0.5">shadowbanned</span>}
                {u.vpn_flag && <span className="font-mono text-[8px] uppercase tracking-[0.15em] border border-sky-400/40 text-sky-400 px-2 py-0.5">vpn detected</span>}
                {u.steam_trust === "low" && <span className="font-mono text-[8px] uppercase tracking-[0.15em] border border-white/25 text-zinc-400 px-2 py-0.5">steam low-trust</span>}
                <span data-testid={`flag-score-${f.id}`} className="ml-auto font-mono font-bold text-lg" style={{ color: f.score >= 70 ? "#f87171" : f.score >= 40 ? "#f59e0b" : "#a1a1aa" }}>{f.score}%</span>
              </div>
              <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">{f.summary}</p>
              {f.evidence?.matched_user && (
                <p className="font-mono text-[9px] text-zinc-500">Matches banned account: <span className="text-red-300">{f.evidence.matched_user}</span></p>
              )}
              <p className="font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em]">flagged {new Date(f.updated_at).toLocaleString()} · account created {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</p>
              <div className="flex gap-2 flex-wrap items-center">
                <input data-testid={`flag-reason-${f.id}`} value={reason[u.id] || ""} onChange={(e) => setReason((r) => ({ ...r, [u.id]: e.target.value }))}
                  placeholder="Reason (logged with your name)" className={`${inputCls} flex-1 min-w-[200px]`} />
                <button data-testid={`flag-hardban-${f.id}`} onClick={() => act(u.id, "hard")} disabled={!!busy}
                  className="bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-2 flex items-center gap-1.5 transition-colors">
                  <Ban className="w-3 h-3" /> Hard ban
                </button>
                <button data-testid={`flag-shadowban-${f.id}`} onClick={() => act(u.id, "shadow")} disabled={!!busy}
                  className="bg-fuchsia-700 hover:bg-fuchsia-600 disabled:opacity-40 text-white font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-2 flex items-center gap-1.5 transition-colors">
                  <EyeOff className="w-3 h-3" /> Shadowban
                </button>
                {u.banned && (
                  <button data-testid={`flag-clear-${f.id}`} onClick={() => act(u.id, "clear")} disabled={!!busy}
                    className="border border-emerald-500/40 text-emerald-400 font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-3 py-2 flex items-center gap-1.5 transition-colors">
                    <CheckCircle2 className="w-3 h-3" /> Clear
                  </button>
                )}
                <button data-testid={`flag-dismiss-${f.id}`} onClick={() => dismiss(f.id)}
                  className="border border-white/15 text-zinc-500 hover:text-zinc-200 font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2 transition-colors">
                  Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* honeypots */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Crosshair className="w-3.5 h-3.5" /> Honeypot invite codes</p>
        <p className="font-mono text-[9px] text-zinc-600 leading-relaxed">
          Honeypots look exactly like real referral codes. Plant them where banned players fish for re-entry — anyone registering through one is
          silently shadowbanned from birth, and the flag tells you where the code was planted.
        </p>
        <div className="flex gap-2">
          <input data-testid="honeypot-label" value={hpLabel} onChange={(e) => setHpLabel(e.target.value)} placeholder="Plant location, e.g. 'griefer discord #invites'" className={inputCls} />
          <button data-testid="honeypot-create" onClick={makeHoneypot}
            className="bg-nc-orange hover:bg-nc-orange/80 text-black font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-4 py-2 flex items-center gap-1.5 shrink-0 transition-colors">
            <Plus className="w-3 h-3" /> Arm code
          </button>
        </div>
        {intel.honeypots.map((h) => (
          <div key={h.id} data-testid={`honeypot-${h.id}`} className="flex items-center gap-3 border border-white/10 bg-black/30 px-3 py-2">
            <span className="font-mono text-[11px] text-zinc-200">{h.code}</span>
            <button data-testid={`honeypot-copy-${h.id}`} onClick={async () => (await safeCopy(h.code)) && toast.success("Copied")} className="text-zinc-500 hover:text-white transition-colors"><Copy className="w-3 h-3" /></button>
            <span className="font-mono text-[9px] text-zinc-500 truncate">{h.label}</span>
            <span className={`ml-auto font-mono text-[8px] uppercase tracking-[0.15em] px-2 py-0.5 border ${h.used_by ? "border-red-400/40 text-red-400" : "border-emerald-500/30 text-emerald-500"}`}>
              {h.used_by ? `tripped by ${h.used_by}` : "armed"}
            </span>
          </div>
        ))}
      </div>

      {/* ban log */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase">Ban log — every action, named admin, timestamped</p>
        {intel.ban_log.length === 0 && <p className="font-mono text-xs text-zinc-600">No actions recorded yet.</p>}
        {intel.ban_log.slice(0, 15).map((b) => (
          <div key={b.id} data-testid={`banlog-${b.id}`} className="flex items-center gap-3 border-b border-white/5 py-2 font-mono text-[10px]">
            <span className={`uppercase tracking-[0.15em] text-[8px] border px-2 py-0.5 ${b.mode === "hard" ? "border-red-400/40 text-red-400" : b.mode === "shadow" ? "border-fuchsia-400/40 text-fuchsia-400" : "border-emerald-500/40 text-emerald-400"}`}>{b.mode}</span>
            <span className="text-zinc-300 truncate">{b.user_email}</span>
            <span className="text-zinc-600 truncate flex-1">{b.reason}</span>
            <span className="text-zinc-600 shrink-0">{b.admin} · {new Date(b.at).toLocaleString()}</span>
          </div>
        ))}
      </div>

      {/* breach protocol + retention */}
      <div className={card}>
        <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><AlertOctagon className="w-3.5 h-3.5" /> GDPR operations</p>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="breach-template-button" onClick={loadBreach}
            className="border border-red-500/40 text-red-400 font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-4 py-2 transition-colors">
            Data breach notice (72h)
          </button>
          <button data-testid="retention-sweep-button" onClick={runSweep} disabled={busy === "sweep"}
            className="border border-white/15 text-zinc-300 font-mono text-[9px] font-bold uppercase tracking-[0.15em] px-4 py-2 transition-colors disabled:opacity-40">
            {busy === "sweep" ? "Sweeping…" : "Run retention sweep now"}
          </button>
        </div>
        <p className="font-mono text-[8px] text-zinc-600 leading-relaxed">
          Retention also runs automatically at 04:00 UTC daily: 24 months inactive → 30-day warning email → automatic erasure. Breach law: affected users must be notified within 72 hours of a confirmed breach.
        </p>
        {breach && (
          <div className="border border-red-500/30 bg-red-500/5 p-4 flex flex-col gap-3">
            <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-red-300">{breach.subject}</p>
            <textarea data-testid="breach-body" rows={6} value={breachBody} onChange={(e) => setBreachBody(e.target.value)} className={`${inputCls} resize-none`} />
            <p className="font-mono text-[8px] text-zinc-600">{breach.note} Empty recipient list = all users.</p>
            <button data-testid="breach-send-button" onClick={sendBreach} disabled={busy === "breach"}
              className="self-start bg-red-600 hover:bg-red-500 disabled:opacity-40 text-white font-mono text-[9px] font-bold uppercase tracking-[0.2em] px-5 py-2.5 transition-colors">
              {busy === "breach" ? "Sending…" : "Send breach notification"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
